import React from 'react';
import { useQuery } from 'react-query';
import { FileText, Briefcase } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ModernDashboard from '../components/ModernDashboard';
import { useTheme, UI_THEMES } from '../contexts/ThemeContext';

const Dashboard = () => {
  const { theme } = useTheme();
  const { data, isLoading } = useQuery('dashboard-summary', async () => {
    const response = await api.get('/dashboard/summary');
    return response.data;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (theme === UI_THEMES.MODERN) {
    return <ModernDashboard data={data} />;
  }

  const invoiceCounts = data?.invoiceCounts || {};
  const caseCounts = data?.caseCounts || {};
  const recentInvoices = (data?.recentInvoices || []).slice(0, 10);
  const recentCases = (data?.recentCases || []).slice(0, 10);

  const stats = [
    {
      name: 'Faktury - Nowe',
      value: invoiceCounts.NEW || 0,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      name: 'Faktury - W realizacji',
      value: invoiceCounts.IN_PROGRESS || 0,
      icon: FileText,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    },
    {
      name: 'Faktury - Przekazane',
      value: invoiceCounts.ASSIGNED || 0,
      icon: FileText,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      name: 'Faktury - Zaksięgowane',
      value: invoiceCounts.DONE || 0,
      icon: FileText,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    },
    {
      name: 'Sprawy - Nowe',
      value: caseCounts.NEW || 0,
      icon: Briefcase,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      name: 'Sprawy - W realizacji',
      value: caseCounts.IN_PROGRESS || 0,
      icon: Briefcase,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100'
    },
    {
      name: 'Sprawy - Przekazane',
      value: caseCounts.ASSIGNED || 0,
      icon: Briefcase,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      name: 'Sprawy - Zakończone',
      value: caseCounts.DONE || 0,
      icon: Briefcase,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
  ];

  return (
    <div className="space-y-6">
      <div className="card mb-6">
        <div className="card-body flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 flex items-center justify-center">
              <img 
                src="/images/cropped-logo-szpital_akredytacja.png"
                alt="Szpital Łapy Logo" 
                className="h-16 w-16 object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-600">Przegląd systemu CRM</p>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="card hover:shadow-lg transition-shadow duration-200">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {stat.value}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg leading-6 font-semibold text-gray-900">
              Ostatnie faktury
            </h3>
          </div>
          <div className="card-body">
            {recentInvoices.length > 0 ? (
              <div className="space-y-3">
                {recentInvoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{invoice.number}</p>
                      <p className="text-xs text-gray-500">{invoice.supplierName}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{invoice.amountGross} {invoice.currency}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        invoice.status === 'DONE' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Brak faktur</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg leading-6 font-semibold text-gray-900">
              Ostatnie sprawy
            </h3>
          </div>
          <div className="card-body">
            {recentCases.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentCases.map((case_) => (
                  <div key={case_.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{case_.title}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      case_.status === 'DONE' ? 'bg-green-100 text-green-800' :
                      case_.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {case_.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Brak spraw</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
